import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { useApiRequest } from '@peanuds/composables';
import type {
  ListBeneficiariesData,
  ListBeneficiariesResponseDtoOutput,
  ListBeneficiaryBankAccountsData,
  ListBeneficiaryBankAccountsResponseDtoOutput,
} from '@peanuds/shared';
import { peanudsApi, withAuthenticatedHeaders } from '@peanuds/shared';

export const useBeneficiariesStore = defineStore('beneficiaries', () => {
  const BENEFICIARIES_PAGE_SIZE = 100;

  type Beneficiary = ListBeneficiariesResponseDtoOutput['data'][number];
  type BankAccountsQuery = ListBeneficiaryBankAccountsData['query'];
  type BankAccountsCountry = Extract<BankAccountsQuery, object>['country'];
  type BankAccountsCurrency = Extract<BankAccountsQuery, object>['currency'];
  type BeneficiaryAccountHolderType = NonNullable<
    ListBeneficiaryBankAccountsResponseDtoOutput['data'][number]['accountHolderType']
  >;
  type BeneficiaryPaymentRail =
    ListBeneficiaryBankAccountsResponseDtoOutput['data'][number]['paymentRail'];
  type BeneficiaryBankAccount = {
    id?: string | null;
    iban?: string | null;
    accountNumber?: string | null;
    bankName?: string | null;
    country?: string | null;
    currency?: string | null;
    routingCodes?: Record<string, string> | null;
    accountHolderType?: BeneficiaryAccountHolderType | null;
    paymentRail?: BeneficiaryPaymentRail;
  };
  const toRoutingCodes = (
    routingCodes: { [key: string]: unknown } | null | undefined,
  ): Record<string, string> | null => {
    if (routingCodes === null || routingCodes === undefined) {
      return null;
    }

    const normalized: Record<string, string> = {};
    for (const [key, value] of Object.entries(routingCodes)) {
      if (typeof value === 'string' && value.trim().length > 0) {
        normalized[key] = value;
      }
    }

    return Object.keys(normalized).length > 0 ? normalized : null;
  };

  const extractBankAccounts = (
    accounts: ListBeneficiaryBankAccountsResponseDtoOutput['data'] | undefined,
  ): BeneficiaryBankAccount[] => {
    const uniqueBankAccounts = new Map<string, BeneficiaryBankAccount>();

    for (const account of accounts ?? []) {
      const iban = account.iban?.trim();
      const accountNumber = account.accountNumber?.trim();
      if (!iban && !accountNumber) {
        continue;
      }

      let accountKey = iban ? `iban:${iban}` : `account-number:${accountNumber}`;
      if (account.id) {
        accountKey = `id:${account.id}`;
      }

      if (uniqueBankAccounts.has(accountKey)) {
        continue;
      }

      uniqueBankAccounts.set(accountKey, {
        id: account.id ?? null,
        iban: iban ?? null,
        accountNumber: accountNumber ?? null,
        bankName: account.bankName ?? null,
        country: account.country ?? null,
        currency: account.currency ?? null,
        routingCodes: toRoutingCodes(account.routingCodes),
        accountHolderType: account.accountHolderType ?? null,
        paymentRail: account.paymentRail,
      });
    }

    return Array.from(uniqueBankAccounts.values());
  };

  const extractIbans = (
    accounts: ListBeneficiaryBankAccountsResponseDtoOutput['data'] | undefined,
  ): string[] => {
    return extractBankAccounts(accounts).flatMap((account) => (account.iban ? [account.iban] : []));
  };

  const createBankAccountsQuery = (input?: {
    country?: BankAccountsCountry;
    currency?: BankAccountsCurrency;
  }): ListBeneficiaryBankAccountsData['query'] => {
    return {
      offset: 0,
      limit: 100,
      ...(input?.country ? { country: input.country } : {}),
      ...(input?.currency ? { currency: input.currency } : {}),
    };
  };

  // ── List beneficiaries ──────────────────────────────────────────────
  const listBeneficiaries = async () => {
    const beneficiaries: Beneficiary[] = [];
    let offset = 0;
    let lastResponse: Awaited<ReturnType<typeof peanudsApi.listBeneficiaries<false>>>;

    while (true) {
      lastResponse = await peanudsApi.listBeneficiaries(
        await withAuthenticatedHeaders<ListBeneficiariesData, false>({
          query: { offset, limit: BENEFICIARIES_PAGE_SIZE },
        }),
      );

      if (lastResponse instanceof Error || lastResponse.error !== undefined) {
        return lastResponse;
      }

      const page = lastResponse.data;
      if (page.offset !== offset) {
        return new Error('Beneficiary pagination returned an unexpected offset');
      }

      beneficiaries.push(...page.data);
      const nextOffset = page.offset + page.data.length;
      if (page.data.length < page.limit || (page.total !== undefined && nextOffset >= page.total)) {
        break;
      }
      if (nextOffset <= offset) {
        return new Error('Beneficiary pagination did not advance');
      }
      offset = nextOffset;
    }

    return {
      ...lastResponse,
      data: {
        ...lastResponse.data,
        offset: 0,
        limit: beneficiaries.length,
        data: beneficiaries,
      },
    };
  };

  const {
    execute: executeFetchBeneficiaries,
    data: beneficiariesResponse,
    isLoading,
    isSuccess,
    error,
  } = useApiRequest({
    request: listBeneficiaries,
  });

  const beneficiaries = computed(() => beneficiariesResponse.value?.data ?? []);
  let pendingBeneficiariesRequest: Promise<
    ListBeneficiariesResponseDtoOutput | null | undefined
  > | null = null;
  let beneficiariesGeneration = 0;

  // ── Bank accounts per beneficiary (single) ─────────────────────────
  const {
    execute: fetchBeneficiaryBankAccounts,
    isLoading: isBeneficiaryBankAccountsLoading,
    error: beneficiaryBankAccountsError,
  } = useApiRequest({
    request: (options: Parameters<typeof peanudsApi.listBeneficiaryBankAccounts>[0]) =>
      peanudsApi.listBeneficiaryBankAccounts(options),
  });

  // ── Bank accounts (IBANs) per beneficiary ─────────────────────────
  // Record<beneficiaryId, primaryIban>
  const bankAccountIbans = ref<Record<string, string>>({});
  // Record<beneficiaryId, allIbans>
  const bankAccountIbansByBeneficiary = ref<Record<string, string[]>>({});
  // Record<beneficiaryId, bankAccounts>
  const bankAccountsByBeneficiary = ref<Record<string, BeneficiaryBankAccount[]>>({});
  // Record<beneficiaryId, accountHolderType>
  const bankAccountHolderTypeByBeneficiary = ref<Record<string, BeneficiaryAccountHolderType>>({});
  const isBankAccountsLoading = ref(false);
  let pendingAllBankAccountsRequest: Promise<boolean> | null = null;
  let hasCompleteBankAccountsSnapshot = false;

  const fetchBeneficiaries = async () => {
    if (pendingBeneficiariesRequest !== null) {
      return pendingBeneficiariesRequest;
    }

    beneficiariesGeneration += 1;
    hasCompleteBankAccountsSnapshot = false;
    const request = executeFetchBeneficiaries();
    pendingBeneficiariesRequest = request;

    try {
      return await request;
    } finally {
      if (pendingBeneficiariesRequest === request) {
        pendingBeneficiariesRequest = null;
      }
    }
  };

  const resolveAccountHolderType = (
    bankAccounts: BeneficiaryBankAccount[],
  ): BeneficiaryAccountHolderType | null => {
    const accountHolderTypes = new Set(
      bankAccounts
        .map((account) => account.accountHolderType)
        .filter(
          (accountHolderType): accountHolderType is BeneficiaryAccountHolderType =>
            accountHolderType === 'individual' || accountHolderType === 'corporate',
        ),
    );

    if (accountHolderTypes.size !== 1) {
      return null;
    }

    return Array.from(accountHolderTypes)[0] ?? null;
  };

  const fetchAllBankAccounts = async () => {
    if (pendingAllBankAccountsRequest !== null) {
      return pendingAllBankAccountsRequest;
    }

    const snapshotGeneration = beneficiariesGeneration;
    const startedWithSettledBeneficiaries = pendingBeneficiariesRequest === null;
    const list = beneficiaries.value;
    if (list.length === 0) {
      hasCompleteBankAccountsSnapshot = startedWithSettledBeneficiaries;
      return true;
    }

    const request = (async () => {
      isBankAccountsLoading.value = true;
      hasCompleteBankAccountsSnapshot = false;

      try {
        const requests = list
          .filter((b: Beneficiary) => b.id)
          .map(async (b: Beneficiary) => {
            const accounts: ListBeneficiaryBankAccountsResponseDtoOutput['data'] = [];
            let offset = 0;

            while (true) {
              const response = await peanudsApi.listBeneficiaryBankAccounts(
                await withAuthenticatedHeaders<ListBeneficiaryBankAccountsData, false>({
                  path: { id: b.id! },
                  query: { ...createBankAccountsQuery(), offset },
                }),
              );

              if (response instanceof Error || response.error !== undefined) {
                return null;
              }

              const page = response.data;
              if (
                page.offset !== offset ||
                (page.data.length === 0 && page.total !== undefined && offset < page.total)
              ) {
                return null;
              }

              accounts.push(...page.data);
              const nextOffset = page.offset + page.data.length;
              if (
                page.data.length < page.limit ||
                (page.total !== undefined && nextOffset >= page.total)
              ) {
                break;
              }
              if (nextOffset <= offset) {
                return null;
              }
              offset = nextOffset;
            }

            return {
              beneficiaryId: b.id!,
              bankAccounts: extractBankAccounts(accounts),
            };
          });

        const results = await Promise.allSettled(requests);

        const newPrimaryIbans: Record<string, string> = {};
        const newIbansByBeneficiary: Record<string, string[]> = {};
        const newBankAccountsByBeneficiary: Record<string, BeneficiaryBankAccount[]> = {};
        let didLoadAllBankAccounts = true;

        for (const result of results) {
          if (result.status !== 'fulfilled' || result.value === null) {
            didLoadAllBankAccounts = false;
            continue;
          }

          const { beneficiaryId, bankAccounts } = result.value;
          if (bankAccounts.length === 0) {
            continue;
          }

          const beneficiaryIbans = bankAccounts.flatMap((account) =>
            account.iban ? [account.iban] : [],
          );
          const accountHolderType = resolveAccountHolderType(bankAccounts);

          newBankAccountsByBeneficiary[beneficiaryId] = bankAccounts;
          newIbansByBeneficiary[beneficiaryId] = beneficiaryIbans;
          const primaryIban = beneficiaryIbans[0];
          if (primaryIban) {
            newPrimaryIbans[beneficiaryId] = primaryIban;
          }

          if (accountHolderType !== null) {
            bankAccountHolderTypeByBeneficiary.value[beneficiaryId] = accountHolderType;
          }
        }

        bankAccountIbans.value = newPrimaryIbans;
        bankAccountIbansByBeneficiary.value = newIbansByBeneficiary;
        bankAccountsByBeneficiary.value = newBankAccountsByBeneficiary;
        if (snapshotGeneration === beneficiariesGeneration) {
          hasCompleteBankAccountsSnapshot =
            didLoadAllBankAccounts && startedWithSettledBeneficiaries;
        }

        return didLoadAllBankAccounts;
      } finally {
        isBankAccountsLoading.value = false;
      }
    })();

    pendingAllBankAccountsRequest = request;

    try {
      return await request;
    } finally {
      if (pendingAllBankAccountsRequest === request) {
        pendingAllBankAccountsRequest = null;
      }
    }
  };

  const ensureAllBankAccountsLoaded = async (): Promise<boolean> => {
    if (hasCompleteBankAccountsSnapshot) {
      return true;
    }

    return fetchAllBankAccounts();
  };

  const fetchBankAccountsForBeneficiary = async (input: {
    beneficiaryId: string;
    country?: BankAccountsCountry;
    currency?: BankAccountsCurrency;
  }): Promise<string[] | null> => {
    if (!input.beneficiaryId) return null;

    const response = await fetchBeneficiaryBankAccounts({
      path: { id: input.beneficiaryId },
      query: createBankAccountsQuery({
        country: input.country,
        currency: input.currency,
      }),
    });

    // If the request errored, do NOT overwrite existing cached IBANs.
    if (!response) return null;

    const bankAccounts = extractBankAccounts(response?.data);
    bankAccountsByBeneficiary.value[input.beneficiaryId] = bankAccounts;

    const accountHolderType = resolveAccountHolderType(bankAccounts);
    if (accountHolderType !== null) {
      bankAccountHolderTypeByBeneficiary.value[input.beneficiaryId] = accountHolderType;
    }

    const ibans = extractIbans(response?.data);
    bankAccountIbansByBeneficiary.value[input.beneficiaryId] = ibans;

    const firstIban = bankAccountIbansByBeneficiary.value[input.beneficiaryId]?.[0];
    if (firstIban) {
      bankAccountIbans.value[input.beneficiaryId] = firstIban;
    } else {
      delete bankAccountIbans.value[input.beneficiaryId];
    }

    return bankAccountIbansByBeneficiary.value[input.beneficiaryId] ?? [];
  };

  const fetchBankAccountsForBeneficiaryId = async (input: {
    beneficiaryId: string;
    country?: BankAccountsCountry;
    currency?: BankAccountsCurrency;
  }): Promise<boolean> => {
    if (!input.beneficiaryId) return false;

    const result = await fetchBankAccountsForBeneficiary({
      beneficiaryId: input.beneficiaryId,
      country: input.country,
      currency: input.currency,
    });

    return result !== null;
  };

  const getIbanForBeneficiary = (beneficiaryId: string): string | undefined => {
    return bankAccountIbans.value[beneficiaryId];
  };

  const getIbansForBeneficiary = (beneficiaryId: string): string[] => {
    const list = bankAccountIbansByBeneficiary.value[beneficiaryId];
    if (list && list.length > 0) return list;

    const primary = getIbanForBeneficiary(beneficiaryId);
    return primary ? [primary] : [];
  };

  const getBankAccountsForBeneficiary = (beneficiaryId: string): BeneficiaryBankAccount[] => {
    const list = bankAccountsByBeneficiary.value[beneficiaryId];
    if (list && list.length > 0) return list;

    return getIbansForBeneficiary(beneficiaryId).map((iban) => ({
      iban,
      bankName: null,
    }));
  };

  const getBankAccountHolderTypeForBeneficiary = (
    beneficiaryId: string,
  ): BeneficiaryAccountHolderType | null => {
    const cachedAccountHolderType = bankAccountHolderTypeByBeneficiary.value[beneficiaryId];
    if (cachedAccountHolderType !== undefined) {
      return cachedAccountHolderType;
    }

    return resolveAccountHolderType(getBankAccountsForBeneficiary(beneficiaryId));
  };

  return {
    // List
    beneficiariesResponse,
    beneficiaries,
    isLoading,
    isSuccess,
    error,
    fetchBeneficiaries,

    // Bank accounts (IBANs)
    bankAccountIbans,
    bankAccountIbansByBeneficiary,
    bankAccountsByBeneficiary,
    bankAccountHolderTypeByBeneficiary,
    isBankAccountsLoading,
    isBeneficiaryBankAccountsLoading,
    beneficiaryBankAccountsError,
    fetchAllBankAccounts,
    ensureAllBankAccountsLoaded,
    fetchBankAccountsForBeneficiary,
    fetchBankAccountsForBeneficiaryId,
    getIbanForBeneficiary,
    getIbansForBeneficiary,
    getBankAccountsForBeneficiary,
    getBankAccountHolderTypeForBeneficiary,
  };
});
